import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Box, Typography, Chip, TableBody,
  TableRow,
  TableCell,
} from "@mui/material";

import { useFeatureRequests } from "./useFeatureRequests";
import ProgressLegend from "./ProgressLegend";
import { ROADMAP_STATUS_OPTIONS, CATEGORY_OPTIONS, STATUS_OPTIONS } from "./featureConfig";
import RoadMapTableHeader from "./RoadMapTableHeader";
import Tooltip from '@mui/material/Tooltip';
import heartLoading from "../../../assests/heartLoading.gif";
import FeatureRequestDialog from "./FeatureRequestDialog";
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import AppliedFilters from "../../common/AppliedFilters";
import { ListFilter } from "lucide-react";

const DEFAULT_ROADMAP_CATEGORIES = ["NEW_FEATURE", "IMPROVEMENT"];

const CATEGORY_MAP = {
  NEW_FEATURE: { label: "New Feature", color: "#2563eb" },
  IMPROVEMENT: { label: "Enhancement", color: "#7c3aed" },
  BUG: { label: "Bug Fix", color: "#dc2626" },
  UI_UX: { label: "UI/UX", color: "#d946ef" },
  PERFORMANCE: { label: "Performance", color: "#ea580c" },
  OTHER: { label: "Other", color: "#6b7280" },
  SECURITY: { label: "Security", color: "#0ea5e9" },
  ONBOARD_PRODUCT: { label: "Onboard Product", color: "#059669" },
};

const findCategoryConfig = (cat) => {
  if (!cat) return CATEGORY_MAP.OTHER;
  const byLabel = Object.values(CATEGORY_MAP).find((c) => c.label.toLowerCase() === String(cat).toLowerCase());
  if (byLabel) return byLabel;
  const key = String(cat).replace(/[\s\-\/]+/g, "_").replace(/__+/g, "_").toUpperCase();
  return CATEGORY_MAP[key] || { label: String(cat), color: CATEGORY_MAP.OTHER.color };
};

const STATUS_PALETTE = {
  "Not Started": { label: "Not Started", bg: "#F1F5F9", color: "#45556C", barColor1: "#CAD5E2", barColor2: "#90A1B9" },
  "In Progress": { label: "In Progress", bg: "#FEF3C6", color: "#BB4D00", barColor1: "#FFB900", barColor2: "#FF8904" },
  "Completed": { label: "Completed", bg: "#D0FAE5", color: "#007A55", barColor1: "#00D492", barColor2: "#00BC7D" },
};

const getDefaultDates = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 30);
  const end = new Date(today);
  end.setDate(end.getDate() + 90);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const Roadmap = () => {
  const {
    enumsLoading, loadRoadmap, roadmapLoading, isDetailOpen,
    setDetailOpen, selectedRequest, canCurrentUserDelete, commentText,
    setCommentText, handleSubmitComment, isSuperuser, adminForm,
    setAdminForm, handleAdminSave,
    savingAdmin,
    handleDeleteRequest, loadingDetails,
    submittingComment, openRequestDetails,
  } = useFeatureRequests();

  const defaults = useMemo(() => getDefaultDates(), []);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [appliedStatus, setAppliedStatus] = useState([]);
  const [appliedCategory, setAppliedCategory] = useState(DEFAULT_ROADMAP_CATEGORIES);

  const [startMonth, setStartMonth] = useState("2025-11");
  const [endMonth, setEndMonth] = useState("2026-04");

  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortBy, setSortBy] = useState(null);

  const ROADMAP_INITIAL_SIZE = 80;
  const ROADMAP_MORE_SIZE = 40;
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadMorePageRef = useRef(Math.ceil(ROADMAP_INITIAL_SIZE / ROADMAP_MORE_SIZE) + 1);
  const loadedCountRef = useRef(0);
  const sentinelRef = useRef(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchBy, setSearchBy] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  const buildRoadmapParams = useCallback(({ category, status, start_date, end_date, sort_by, sort_order, search_by, search_value }) => ({
    category: category ?? appliedCategory,
    status: status ?? appliedStatus,
    start_date: start_date ?? startDate,
    end_date: end_date ?? endDate,
    sort_by: sort_by ?? sortBy,
    sort_order: sort_order ?? sortOrder,
    search_by: search_by ?? searchBy,
    search_value: search_value ?? searchValue,
  }), [appliedCategory, appliedStatus, startDate, endDate, sortBy, sortOrder, searchBy, searchValue]);

  const fetchRoadmap = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const params = buildRoadmapParams(overrides);
      const res = await loadRoadmap({ ...params, page: 1, page_size: ROADMAP_INITIAL_SIZE });
      if (res) {
        const loaded = res.features || [];
        const serverTotal = typeof res.total === "number" ? res.total : (typeof res.totalFeatures === "number" ? res.totalFeatures : 0);
        setFeatures(loaded);
        loadedCountRef.current = loaded.length;
        setHasMore(serverTotal > loaded.length);
        loadMorePageRef.current = Math.ceil(ROADMAP_INITIAL_SIZE / ROADMAP_MORE_SIZE) + 1;
      } else {
        setFeatures([]);
        setHasMore(false);
      }
    } catch (err) {
      setFeatures([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [buildRoadmapParams, loadRoadmap]);

  const loadMoreRoadmap = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    try {
      const res = await loadRoadmap({
        category: appliedCategory, status: appliedStatus,
        start_date: startDate, end_date: endDate,
        sort_by: sortBy, sort_order: sortOrder,
        search_by: searchBy, search_value: searchValue,
        page: loadMorePageRef.current, page_size: ROADMAP_MORE_SIZE,
      });
      if (res) {
        const newFeats = res.features || [];
        const serverTotal = typeof res.total === "number" ? res.total : (typeof res.totalFeatures === "number" ? res.totalFeatures : 0);
        setFeatures((prev) => [...prev, ...newFeats]);
        loadedCountRef.current += newFeats.length;
        setHasMore(loadedCountRef.current < serverTotal);
        loadMorePageRef.current += 1;
      }
    } catch (err) {
      console.error("Failed to load more roadmap features:", err);
    } finally {
      setIsFetchingMore(false);
    }
  }, [isFetchingMore, hasMore, appliedCategory, appliedStatus, startDate, endDate, sortBy, sortOrder, searchBy, searchValue, loadRoadmap]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetchingMore) loadMoreRoadmap();
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, loadMoreRoadmap]);

  const canDeleteSelected = selectedRequest ? canCurrentUserDelete(selectedRequest) : false;

  useEffect(() => {
    fetchRoadmap();
  }, [fetchRoadmap]);

  const filteredFeatures = features;

  const normalizeTimelineRange = (timeline) => {
    if (!timeline) return null;
    let start = null;
    let end = null;
    if (Array.isArray(timeline)) {
      timeline.forEach((t) => {
        const s = t?.startDate ? new Date(t.startDate) : null;
        const e = t?.endDate ? new Date(t.endDate) : null;
        if (s && (!start || s < start)) start = s;
        if (e && (!end || e > end)) end = e;
      });
    } else {
      start = timeline.startDate ? new Date(timeline.startDate) : null;
      end = timeline.endDate ? new Date(timeline.endDate) : null;
    }
    if (!start || !end) return null;
    return { start, end };
  };

  const computeBarPercents = (featureRange) => {
    const rangeStart = new Date(startMonth + "-01");
    const rangeEnd = new Date(new Date(endMonth + "-01").getFullYear(), new Date(endMonth + "-01").getMonth() + 1, 0);
    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / msPerDay) + 1);

    const fStart = featureRange.start;
    const fEnd = featureRange.end;

    const clampedStart = fStart < rangeStart ? rangeStart : fStart;
    const clampedEnd = fEnd > rangeEnd ? rangeEnd : fEnd;
    if (clampedEnd < rangeStart || clampedStart > rangeEnd) return null;

    const offsetDays = Math.round((clampedStart - rangeStart) / msPerDay);
    const widthDays = Math.round((clampedEnd - clampedStart) / msPerDay) + 1;

    const left = (offsetDays / totalDays) * 100;
    const width = (widthDays / totalDays) * 100;
    return { left, width };
  };

  const handleApplyFilters = (filters) => {
    const { startDate: sd, endDate: ed, status, category } = filters;
    const resolvedCategory = category && category.length > 0 ? category : DEFAULT_ROADMAP_CATEGORIES;
    setStartDate(sd);
    setEndDate(ed);
    setAppliedStatus(status || []);
    setAppliedCategory(resolvedCategory);
    fetchRoadmap({ category: resolvedCategory, status: status || [], start_date: sd, end_date: ed });
  };

  const handleResetFilters = () => {
    const { startDate: newStart, endDate: newEnd } = getDefaultDates();
    setStartDate(newStart);
    setEndDate(newEnd);
    setAppliedStatus([]);
    setAppliedCategory(DEFAULT_ROADMAP_CATEGORIES);
    fetchRoadmap({ category: DEFAULT_ROADMAP_CATEGORIES, status: [], start_date: newStart, end_date: newEnd });
  };

  const handleSortChange = (sort_by, sort_order) => {
    setSortBy(sort_by);
    setSortOrder(sort_order);
    fetchRoadmap({ sort_by, sort_order, search_by: searchBy, search_value: searchValue });
  };

  const onSearchChange = (key, value) => {
    setSearchBy(key);
    setSearchValue(value);
    fetchRoadmap({ search_by: key || searchBy, search_value: value || searchValue });
  };

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filterSections = useMemo(
    () => [
      {
        fields: [
          {
            type: "date",
            key: "startDate",
            label: "Start Date",
            defaultValue: defaults.startDate,
          },
          {
            type: "date",
            key: "endDate",
            label: "End Date",
            defaultValue: defaults.endDate,
          },
          {
            type: "multiselect",
            key: "status",
            label: "Status",
            options: ROADMAP_STATUS_OPTIONS.filter((o) => o.value !== "ALL"),
          },
          {
            type: "multiselect",
            key: "category",
            label: "Category",
            options: CATEGORY_OPTIONS.filter((o) => o.value !== "ALL"),
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const activeFilterCount = appliedStatus.length + (
    appliedCategory.length !== DEFAULT_ROADMAP_CATEGORIES.length ? appliedCategory.length : 0
  );

  const appliedChips = [
    { label: "Date Range", value: `${formatDateLabel(startDate)} → ${formatDateLabel(endDate)}` },
    ...appliedStatus.map((s) => ({
      label: "Status",
      value: ROADMAP_STATUS_OPTIONS.find((o) => o.value === s)?.label || s,
    })),
    ...appliedCategory.map((c) => ({
      label: "Category",
      value: CATEGORY_OPTIONS.find((o) => o.value === c)?.label || c,
    })),
  ];

  return (
    <Box sx={{ width: "100%", fontFamily: '"Poppins", sans-serif', padding: "16px 16px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "24px" }}>
            Product Roadmap
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "14px" }}>
            Timeline view of feature development across categories
          </Typography>
        </Box>

        <button
          onClick={() => setFilterDrawerOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-[45px] whitespace-nowrap transition-colors mt-1"
        >
          <ListFilter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-[#bb1ccc] text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>
      </Box>

      <AppliedFilters chips={appliedChips} />

      <ProgressLegend />

      <Box sx={{
        overflow: "auto",
        width: "100%",
        minWidth: 980,
        maxHeight: "calc(100vh - 300px)",
        borderCollapse: "collapse",
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "10px"
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <RoadMapTableHeader
            onSearchChange={onSearchChange}
            onSortChange={handleSortChange}
            setStartMonth={setStartMonth}
            setEndMonth={setEndMonth}
            startMonth={startMonth}
            endMonth={endMonth}
            setSortBy={setSortBy}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            sortBy={sortBy}
            startDate={startDate}
            endDate={endDate}
          />

          <TableBody>
            {loading || roadmapLoading || enumsLoading ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, borderBottom: "1px solid #E6EDF3", textAlign: 'center' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="50vh"
                    flexDirection="column"
                    gap={2}
                  >
                    <img src={heartLoading} alt="Loading..."style={{ width : 200}} />
                    <Typography color="text.secondary" sx={{ fontSize: "18px" }}>
                      Loading feature requests...
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : filteredFeatures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} style={{ padding: 24, borderBottom: "1px solid #E6EDF3", textAlign: 'center' }}>
                  No features in this range.
                </TableCell>
              </TableRow>
            ) : (
              filteredFeatures.map((f) => {
                const timelineSource = f.timeline || f.timelines || (f.startDate && f.endDate ? { startDate: f.startDate, endDate: f.endDate } : null);
                const featureRange = normalizeTimelineRange(timelineSource);
                const percents = featureRange ? computeBarPercents(featureRange) : null;

                return (
                  <TableRow onClick={() => openRequestDetails(f)} key={f.id} hover style={{ borderTop: "1px solid #eef2f7", cursor: "pointer" }}>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <div style={{ fontWeight: "normal" }}>{f.title}</div>
                    </TableCell>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{f.owner}</div>
                    </TableCell>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      {(() => {
                        const categoryConfig = findCategoryConfig(f.category);
                        return (
                          <Chip
                            label={categoryConfig.label}
                            size="small"
                            sx={{
                              fontSize: "12px",
                              backgroundColor: `${categoryConfig.color}15`,
                              color: categoryConfig.color,
                              minWidth: 130,
                              justifyContent: "center",
                              borderRadius: 5,
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell style={{ padding: "12px 8px", verticalAlign: "middle", width: 720 }}>
                      <div style={{ position: "relative", height: 28, width: "100%" }}>
                        {f.timeline?.startDate && f.timeline?.endDate && percents && (
                          <Tooltip
                            title={`${formatDateLabel(f.timeline.startDate)} → ${formatDateLabel(f.timeline.endDate)}`}
                            arrow
                            placement="top"
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: `${percents.left}%`,
                                width: `${percents.width}%`,
                                height: 20,
                                top: "50%",
                                transform: "translateY(-50%)",
                                borderRadius: 6,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                background: `linear-gradient(90deg, ${STATUS_PALETTE[f.status].barColor1}, ${STATUS_PALETTE[f.status].barColor2})`,
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell style={{ padding: "12px", verticalAlign: "middle" }}>
                      <Chip
                        label={f.status}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          minWidth: 90,
                          justifyContent: "center",
                          color: STATUS_PALETTE[f.status].color,
                          backgroundColor: `${STATUS_PALETTE[f.status].bg}`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </table>

        <div ref={sentinelRef} style={{ height: 1 }} />
        {isFetchingMore && (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={1}>
            <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
            <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>Loading more...</Typography>
          </Box>
        )}
        {!hasMore && filteredFeatures.length > 0 && !loading && (
          <Box display="flex" justifyContent="center" py={1.5}>
            <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>All results loaded</Typography>
          </Box>
        )}
      </Box>

      <FeatureRequestDialog
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        canDeleteRequest={canDeleteSelected}
        commentValue={commentText}
        onCommentChange={setCommentText}
        onSubmitComment={handleSubmitComment}
        isSuperuser={isSuperuser}
        adminForm={adminForm}
        onAdminFormChange={(updates) => setAdminForm((prev) => ({ ...prev, ...updates }))}
        onAdminSave={handleAdminSave}
        onDelete={handleDeleteRequest}
        saving={savingAdmin}
        statusOptions={STATUS_OPTIONS}
        loadingDetails={loadingDetails}
        submittingComment={submittingComment}
      />

      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={{ startDate, endDate, status: appliedStatus, category: appliedCategory }}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        flat
        title="Roadmap Filters"
        subtitle="Filter by date, status & category"
      />
    </Box>
  );
};

export default Roadmap;
